import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiArrowRight, FiCheck, FiTrendingUp, FiUsers, FiMail, FiBarChart, FiTarget } = FiIcons;

const DigitalMarketingService = () => {
  const services = [
    {
      icon: FiUsers,
      title: 'Social Media Strategy',
      description: 'Comprehensive social media strategies that build communities and drive engagement across all platforms.'
    },
    {
      icon: FiTarget,
      title: 'Content Creation',
      description: 'Compelling content that tells your story, engages your audience, and drives meaningful interactions.'
    },
    {
      icon: FiTrendingUp,
      title: 'Google Ads Management',
      description: 'Strategic Google Ads campaigns that maximize ROI and drive qualified traffic to your business.'
    },
    {
      icon: FiMail,
      title: 'Email Marketing',
      description: 'Personalized email campaigns that nurture leads and convert prospects into loyal customers.'
    },
    {
      icon: FiBarChart,
      title: 'Analytics & Reporting',
      description: 'Detailed analytics and reporting that provide insights into campaign performance and ROI.'
    }
  ];

  const platforms = [
    {
      name: 'Facebook & Instagram',
      description: 'Visual storytelling and community building'
    },
    {
      name: 'LinkedIn',
      description: 'Professional networking and B2B marketing'
    },
    {
      name: 'Google Ads',
      description: 'Search and display advertising'
    },
    {
      name: 'Email Platforms',
      description: 'Mailchimp, ConvertKit, and more'
    },
    {
      name: 'Analytics Tools',
      description: 'Google Analytics, Facebook Insights'
    },
    {
      name: 'Content Management',
      description: 'Scheduling and publishing tools'
    }
  ];

  const packageFeatures = [
    'Social media strategy development',
    'Content calendar and planning',
    'Custom graphic design',
    'Copywriting and content creation',
    'Community management',
    'Paid advertising campaigns',
    'Email marketing setup',
    'Performance tracking and reporting',
    'Monthly strategy reviews',
    'Ongoing optimization'
  ];

  const process = [
    {
      step: '01',
      title: 'Audit & Strategy',
      description: 'Analyze your current digital presence and develop a comprehensive marketing strategy.',
      timeline: '1 week'
    },
    {
      step: '02',
      title: 'Campaign Setup',
      description: 'Set up social media profiles, advertising accounts, and tracking systems.',
      timeline: '1 week'
    },
    {
      step: '03',
      title: 'Content Creation',
      description: 'Develop engaging content including graphics, copy, and video materials.',
      timeline: 'Ongoing'
    },
    {
      step: '04',
      title: 'Campaign Launch',
      description: 'Launch campaigns across all selected platforms with proper targeting and optimization.',
      timeline: '1 week'
    },
    {
      step: '05',
      title: 'Monitor & Optimize',
      description: 'Continuously monitor performance and optimize campaigns for better results.',
      timeline: 'Ongoing'
    }
  ];

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              Digital <span className="text-purple-600">Marketing</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-8">
              Strategic digital campaigns that drive engagement, increase brand awareness, and grow your business in the competitive online landscape.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Campaign <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-teal-600 transition-all duration-300"
              >
                View Our Work
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              What We Offer
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive digital marketing services that help you reach your target audience and achieve your business goals.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-lg card-hover border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                  <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-poppins font-semibold text-xl text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="font-inter text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Platforms & Tools We Use
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              We leverage the best digital marketing platforms and tools to maximize your reach and campaign effectiveness.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-6 shadow-md"
              >
                <h3 className="font-poppins font-semibold text-lg text-gray-900 mb-2">
                  {platform.name}
                </h3>
                <p className="font-inter text-gray-600 text-sm">
                  {platform.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-4">
              Our Marketing Process
            </h2>
            <p className="font-inter text-lg text-gray-600 max-w-2xl mx-auto">
              Our data-driven approach ensures your digital marketing campaigns deliver measurable results and continuous improvement.
            </p>
          </motion.div>
          <div className="space-y-8">
            {process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`flex flex-col lg:flex-row items-center gap-8 ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                <div className="flex-1">
                  <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-teal-600 rounded-lg flex items-center justify-center mr-4">
                        <span className="font-poppins font-bold text-white">{step.step}</span>
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-xl text-gray-900">
                          {step.title}
                        </h3>
                        <p className="font-inter text-purple-600 font-medium text-sm">
                          {step.timeline}
                        </p>
                      </div>
                    </div>
                    <p className="font-inter text-gray-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Package & Pricing */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-poppins font-bold text-3xl md:text-4xl text-gray-900 mb-6">
                Complete Marketing Package
              </h2>
              <p className="font-inter text-lg text-gray-600 mb-8 leading-relaxed">
                Our comprehensive digital marketing package includes everything you need to establish a strong online presence and drive meaningful business growth.
              </p>
              <div className="space-y-3 mb-8">
                {packageFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                    <span className="font-inter text-gray-600">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-xl p-6 text-white mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-inter text-sm opacity-90">Starting Investment</p>
                    <p className="font-poppins font-bold text-3xl">From R8,000/month</p>
                  </div>
                  <div className="text-right">
                    <p className="font-inter text-sm opacity-90">Setup Time</p>
                    <p className="font-poppins font-semibold text-lg">2-3 weeks</p>
                  </div>
                </div>
              </div>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Get Started Today <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-w-4 aspect-h-3">
                <img
                  src="https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=800&h=600&fit=crop"
                  alt="Digital marketing analytics"
                  className="w-full h-96 object-cover rounded-2xl shadow-xl"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white rounded-2xl shadow-xl flex items-center justify-center">
                <div className="text-center">
                  <div className="font-poppins font-bold text-2xl text-purple-600">300%</div>
                  <div className="font-inter text-sm text-gray-600">Avg. Growth</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Grow Your Business Online?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's create digital marketing campaigns that drive real results and help your business thrive in the digital world.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-purple-700 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Marketing Campaign <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                See Our Results
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DigitalMarketingService;